import React, { useState, useMemo, useCallback } from "react";
import { CloseButton, TextInput, Text, Group, Box, Button, Stack, Pill, ScrollArea, Loader, UnstyledButton, Divider } from "@mantine/core";
import { StackedModal } from "@em/ui";
import { useTranslation } from "react-i18next";

import { getMostContrastingColor } from "@/utils/colorHelpers";
import { Tag } from "@/utils/api/TagService";
import { useTagsContext } from "@/contexts/TagsContext";

import { AppIcon } from "../AppIcon";

// ============================================
// Types
// ============================================

export interface TagsPickerModalProps {
  opened: boolean;
  onClose: () => void;

  /** Current selected tag IDs */
  value: string[];

  /** Callback when selection is confirmed */
  onConfirm: (tagIds: string[]) => void;

  /** Max tags allowed */
  maxTags?: number;

  /** Whether to allow creating new tags inline */
  allowCreate?: boolean;
}

// ============================================
// Constants
// ============================================

const DEFAULT_MAX_TAGS = 5;

// ============================================
// Component
// ============================================

export const TagsPickerModal: React.FC<TagsPickerModalProps> = ({
  opened,
  onClose,
  value,
  onConfirm,
  maxTags = DEFAULT_MAX_TAGS,
  allowCreate = true,
}) => {
  const { t } = useTranslation(["tags", "common"]);
  const {
    activeTags,
    isLoaded: tagsLoaded,
    getTagById,
    createTagInline,
  } = useTagsContext();

  // Local selection state (committed on Done)
  const [localSelection, setLocalSelection] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState("");
  const [creatingTagName, setCreatingTagName] = useState<string | null>(null);

  // Sync local selection when modal opens
  React.useEffect(() => {
    if (opened) {
      setLocalSelection(value);
      setSearchValue("");
      setCreatingTagName(null);
    }
  }, [opened, value]);

  const isMaxReached = localSelection.length >= maxTags;

  // Get selected tag objects for pill display
  const selectedTags = useMemo(() => {
    return localSelection
      .map((id) => getTagById(id))
      .filter((tag): tag is Tag => tag !== undefined);
  }, [localSelection, getTagById]);

  // Filter available tags
  const filteredTags = useMemo(() => {
    const searchLower = searchValue.toLowerCase().trim();

    return activeTags.filter((tag) => {
      if (searchLower && tag.name) {
        return tag.name.toLowerCase().includes(searchLower);
      }
      return true;
    });
  }, [activeTags, searchValue]);

  // Check if exact match exists (for create option)
  const exactMatchExists = useMemo(() => {
    if (!searchValue.trim()) return true;
    const searchLower = searchValue.toLowerCase().trim();
    return activeTags.some((tag) => tag.name?.toLowerCase() === searchLower);
  }, [activeTags, searchValue]);

  // The action is always VISIBLE while creating is possible (so it's discoverable
  // before typing); it's only ENABLED once the name is new and non-empty.
  const showCreateOption = allowCreate && !isMaxReached;
  const canCreate = showCreateOption && searchValue.trim().length > 0 && !exactMatchExists;

  // Toggle tag selection
  const handleToggleTag = useCallback(
    (tagId: string) => {
      setLocalSelection((prev) => {
        if (prev.includes(tagId)) {
          return prev.filter((id) => id !== tagId);
        }
        if (prev.length >= maxTags) return prev;
        return [...prev, tagId];
      });
    },
    [maxTags]
  );

  // Remove tag from selection
  const handleRemoveTag = useCallback((tagId: string) => {
    setLocalSelection((prev) => prev.filter((id) => id !== tagId));
  }, []);

  // Create new tag
  const handleCreateTag = useCallback(async () => {
    const tagName = searchValue.trim();
    if (!tagName || isMaxReached) return;

    setCreatingTagName(tagName);
    setSearchValue("");

    try {
      const newTag = await createTagInline(tagName);
      if (newTag?.id) {
        setLocalSelection((prev) => [...prev, newTag.id!]);
      }
    } catch (error) {
      console.error("Failed to create tag:", error);
    } finally {
      setCreatingTagName(null);
    }
  }, [searchValue, isMaxReached, createTagInline]);

  // Confirm and close
  const handleDone = useCallback(() => {
    onConfirm(localSelection);
    onClose();
  }, [localSelection, onConfirm, onClose]);

  return (
    <StackedModal
      opened={opened}
      onClose={onClose}
      title={t("tags:selector.modalTitle", { defaultValue: "Select Tags" })}
      fullScreen
      zIndex={1000}
      styles={{
        body: {
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 60px)",
          padding: 0,
        },
      }}
    >
      {/* Search input */}
      <Box px="md" pt="xs" pb="sm" style={{ flexShrink: 0 }}>
        <TextInput
          placeholder={t("tags:selector.searchPlaceholder", {
            defaultValue: "Search tags...",
          })}
          value={searchValue}
          onChange={(e) => setSearchValue(e.currentTarget.value)}
          leftSection={<AppIcon iconName="Search" size={18} />}
          rightSection={
            searchValue ? (
              <CloseButton
                aria-label={t("common:buttons.clear", { defaultValue: "Clear" })}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setSearchValue("")}
              />
            ) : undefined
          }
          rightSectionPointerEvents="auto"
          size="md"
          autoFocus
        />
      </Box>

      {/* Create-in-place — pinned so it's reachable without scrolling the list. */}
      {showCreateOption && (
        <Box px="md" pb="sm" style={{ flexShrink: 0 }}>
          <UnstyledButton
            onClick={handleCreateTag}
            disabled={!canCreate || !!creatingTagName}
            w="100%"
            py="xs"
            px="xs"
            style={{ borderRadius: 8, opacity: canCreate ? 1 : 0.55, cursor: canCreate ? "pointer" : "default" }}
          >
            <Group gap="sm" wrap="nowrap">
              {creatingTagName ? (
                <Loader size={18} />
              ) : (
                <AppIcon iconName="Plus" size={18} color="var(--mantine-color-brand-6)" />
              )}
              <Text size="md" c="brand.7" fw={600}>
                {canCreate
                  ? t("tags:selector.createOption", {
                      name: searchValue.trim(),
                      defaultValue: `Create "${searchValue.trim()}"`,
                    })
                  : t("tags:selector.createHint", {
                      defaultValue: "Type a name to create a new tag",
                    })}
              </Text>
            </Group>
          </UnstyledButton>
        </Box>
      )}

      {/* Selected tags pills */}
      {selectedTags.length > 0 && (
        <Box px="md" pb="sm" style={{ flexShrink: 0 }}>
          <Group gap="xs" wrap="wrap">
            {selectedTags.map((tag) => {
              const tagColor = tag.color || "#6B7280";
              return (
                <Pill
                  key={tag.id}
                  withRemoveButton
                  onRemove={() => tag.id && handleRemoveTag(tag.id)}
                  size="md"
                  styles={{
                    root: {
                      backgroundColor: tagColor,
                      color: getMostContrastingColor(tagColor),
                    },
                    remove: {
                      color: getMostContrastingColor(tagColor),
                    },
                  }}
                >
                  {tag.name}
                </Pill>
              );
            })}
          </Group>
          {isMaxReached && (
            <Text size="xs" c="dimmed" mt={4}>
              {t("tags:selector.maxReached", {
                max: maxTags,
                defaultValue: `Maximum ${maxTags} tags`,
              })}
            </Text>
          )}
        </Box>
      )}

      <Divider />

      {/* Tag list */}
      <ScrollArea style={{ flex: 1 }} type="auto" offsetScrollbars>
        <Stack gap={0} px="md" py="xs">
          {/* Loading state */}
          {!tagsLoaded && (
            <Group gap="xs" justify="center" p="lg">
              <Loader size={16} />
              <Text size="md" c="dimmed">
                {t("common:loading")}
              </Text>
            </Group>
          )}

          {/* Creating indicator */}
          {creatingTagName && !showCreateOption && (
            <Group gap="xs" py="sm" px="xs">
              <Loader size={16} />
              <Text size="sm" c="dimmed">
                {t("tags:selector.creating", {
                  name: creatingTagName,
                  defaultValue: `Creating "${creatingTagName}"...`,
                })}
              </Text>
            </Group>
          )}

          {/* Tag options */}
          {filteredTags.map((tag) => {
            if (!tag.id) return null;
            const tagColor = tag.color || "#6B7280";
            const isSelected = localSelection.includes(tag.id);
            const isDisabledOption = !isSelected && isMaxReached;

            return (
              <UnstyledButton
                key={tag.id}
                onClick={() => tag.id && handleToggleTag(tag.id)}
                disabled={isDisabledOption}
                py="sm"
                px="xs"
                style={{
                  borderRadius: 8,
                  opacity: isDisabledOption ? 0.4 : 1,
                }}
              >
                <Group gap="sm" wrap="nowrap" justify="space-between">
                  <Group gap="sm" wrap="nowrap">
                    <Box
                      style={{
                        width: 14,
                        height: 14,
                        borderRadius: "50%",
                        backgroundColor: tagColor,
                        flexShrink: 0,
                      }}
                    />
                    <Text size="md">{tag.name}</Text>
                  </Group>
                  {isSelected && (
                    <AppIcon iconName="Check" size={18} color="blue" />
                  )}
                </Group>
              </UnstyledButton>
            );
          })}

          {/* No results */}
          {tagsLoaded &&
            filteredTags.length === 0 &&
            !creatingTagName && (
              <Text size="sm" c="dimmed" ta="center" py="lg">
                {searchValue.trim()
                  ? t("common:form.noResults", {
                    defaultValue: "No tags found",
                  })
                  : t("tags:selector.noTags", {
                    defaultValue: "No tags available",
                  })}
              </Text>
            )}
        </Stack>
      </ScrollArea>

      {/* Done button (sticky footer) */}
      <Box
        px="md"
        py="md"
        style={{
          flexShrink: 0,
          borderTop: "1px solid var(--mantine-color-gray-3)",
          paddingBottom: "calc(16px + env(safe-area-inset-bottom, 0px))",
          backgroundColor: "var(--mantine-color-body)",
        }}
      >
        <Button fullWidth size="lg" onClick={handleDone}>
          {t("common:buttons.done", { defaultValue: "Done" })}
          {localSelection.length > 0 && ` (${localSelection.length})`}
        </Button>
      </Box>
    </StackedModal>
  );
};